import './topBar.scss';
import { commonLang } from '@umu/ukit-lang';
import React, { useEffect, useState, useRef } from 'react';
import { isInUse } from '@umu/umu-util';
import umuWebview from '@umu/umu-webview';
import { isInIframe, $window } from '@umu/umu-dom';
import { IUserInfo, PlatePermissionType } from '@umu/umu-types';
import { If, Button } from 'common/src/components';
import { render as renderHeader } from '@umu/common-header';

import TopBarLogo from './topBarLogo/topBarLogo';
import UserMenu from '../@userMenu/userMenu';
import { isInMiniProgram as checkIfInMiniProgram, isFromLive } from '../../@utils';
import MiniProgramBar from './@miniProgramBar/miniProgramBar';
import {
    WrapperUseMobileIframeLandscapeDisplayManager,
    IWrapperUseMobileIframeLandscapeDisplayProps,
} from '../../services/responsiveDisplay/course';
import { getAppData } from '../departmentAndMemberSearch/service/pageData';
import { Iconfont } from '@umu/umu-design';
import { isInMobile, isMobileDomain } from '@umu/umu-ua';

interface ITopBarProps extends IWrapperUseMobileIframeLandscapeDisplayProps {
    title?: string;
    userInfo?: IUserInfo;
    backUrl?: string;
    logoHref?: string;
    onTitleClick?: () => void;
    children?: JSX.Element;
    isShowUserMenu?: boolean; // 兼容搜索
    isForceShow?: boolean;
    isProhibitChangePlate?: boolean;
    isShowInIframe?: boolean; // 在Iframe 下仍然显示
    customizedLogo?: string;
    customizedBackIcon?: JSX.Element;
    titleSuffix?: JSX.Element;
    customizedUserMenu?: JSX.Element;
}

const isInWebview = umuWebview?.UA_INFO?.isUMUWebview;
const isInnerIframe = isInIframe();
function useIsTextTruncated() {
    const ref = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const element = ref.current;
        const checkTruncation = () => {
            if (!element) {
                return;
            }
            if (element.scrollWidth > element.clientWidth) {
                setIsTruncated(true);
            } else {
                setIsTruncated(false);
            }
        };

        checkTruncation();
        // 可选：监听窗口大小更改，以便在浏览器窗口大小变化时重新检查。同时可以选择 debounce 以提高性能。
        $window.addEventListener('resize', checkTruncation);
        return () => {
            $window.removeEventListener('resize', checkTruncation);
        };
    }, [ref]);

    return { ref, isTruncated };
}
function TopBar(props: ITopBarProps) {
    const {
        title = '',
        backUrl,
        children,
        customizedLogo,
        customizedBackIcon,
        isProhibitChangePlate = false,
        titleSuffix,
    } = props;

    const [isInLiveMiniApp, setIsInLiveMiniApp] = useState(false);
    const [isShowFullTitle, setIsShowFullTitle] = useState(false);
    const { ref, isTruncated } = useIsTextTruncated();
    const { userInfo } = getAppData();

    const hasUSEPermission = [PlatePermissionType.USE_ONLY, PlatePermissionType.UMU_AND_USE].includes(
        userInfo?.platform_permission as PlatePermissionType,
    );
    const isInUSE = isProhibitChangePlate ? false : isInUse() && hasUSEPermission;

    useEffect(() => {
        // 环境变量，初始化获取即可。
        checkIfInMiniProgram((is) => {
            setIsInLiveMiniApp(is && isFromLive());
        });
    }, []);

    useEffect(() => {
        if (isProhibitChangePlate) {
            return;
        }
        const header = document.querySelector('.use-header');
        if (isInUSE && header) {
            const { siteHost, SYSTEM, Lang, userInfo } = getAppData();
            const platform_permission = (userInfo as any).platform_permission;
            renderHeader({
                hostEl: header as HTMLElement,
                userInfo: userInfo as any,
                enterpriseInfo: userInfo?.enterprise_info as any,
                siblingSubHeader: true,
                siteHost: siteHost,
                system: SYSTEM,
                lang: Lang,
                isCAdministrator: false,
                isOnlyUseEnterpriseAccount: platform_permission === PlatePermissionType.USE_ONLY,
                isOnlyUmuEnterpriseAccount: platform_permission === PlatePermissionType.UMU,
                isUseAndUmuEnterpriseAccount: platform_permission === PlatePermissionType.UMU_AND_USE,
            });
        }
    }, [isInUSE, isProhibitChangePlate]);

    const onTitleClick = () => {
        if (props.onTitleClick) {
            props.onTitleClick();
        }
    };

    const handleTitleOperationClick = (evt: React.MouseEvent<HTMLDivElement>) => {
        evt?.stopPropagation();
        setIsShowFullTitle(!isShowFullTitle);
    };

    const renderContent = () => {
        return (
            <div className="nav-item nav-content">
                <If condition={!!title}>
                    <div
                        onClick={onTitleClick}
                        style={{
                            cursor: `${!!props.onTitleClick ? 'pointer' : 'default'}`,
                        }}
                        className={`nav-item__title-container ${
                            titleSuffix ? 'nav-item__title--with-suffix-container' : ''
                        }`}
                    >
                        <div className="nav-item_title" ref={ref}>
                            {title}
                        </div>
                        <If condition={isTruncated}>
                            <div
                                className="nav-item_title-operation"
                                style={{
                                    transform: `rotate(${isShowFullTitle ? 180 : 0}deg)`,
                                }}
                                onClick={handleTitleOperationClick}
                            >
                                <Iconfont type="DropDown" />
                            </div>
                        </If>
                        {titleSuffix ? <div className="nav-item_title-suffix">{titleSuffix}</div> : null}
                    </div>
                </If>
                {!title && children ? children : null}
            </div>
        );
    };

    const renderUserMenu = () => {
        const { isShowUserMenu, customizedUserMenu } = props;

        if (customizedUserMenu) {
            return <div className="nav-item nav-action">{customizedUserMenu}</div>;
        }

        if (typeof isShowUserMenu !== 'undefined' && isShowUserMenu === false) {
            return null;
        }
        return (
            <div className="nav-item nav-action">
                <UserMenu />
            </div>
        );
    };

    const render = () => {
        if (isInnerIframe && !props.isShowInIframe) {
            return null;
        }

        // web课程页，隐藏头
        if (isInnerIframe && props._isDisplayInIframeModeForCourse && !props.isShowInIframe) {
            return null;
        }
        if (isInWebview && !props.isForceShow) {
            return null;
        }
        if (isInLiveMiniApp) {
            return <MiniProgramBar></MiniProgramBar>;
        }
        if (isInUSE && !(isInMobile() || isMobileDomain())) {
            return <div className="use-header"></div>;
        }

        return (
            <div className="top-bar">
                <div className="top-bar-nav">
                    {customizedBackIcon}
                    <If condition={!customizedBackIcon}>
                        <>
                            <If condition={!!backUrl}>
                                <div className="nav-item nav-back">
                                    <Button
                                        className=""
                                        href={backUrl}
                                        alias="text"
                                        iconBefore={<i className="icon icon-arrow arrow1 blue left size-s"></i>}
                                    >
                                        {commonLang('返回')}
                                    </Button>
                                </div>
                            </If>
                            <If condition={!backUrl}>
                                <div className="nav-item nav-logo">
                                    <TopBarLogo customizedLogo={customizedLogo} logoHref={props.logoHref} />
                                </div>
                            </If>
                        </>
                    </If>
                    {renderContent()}
                    {renderUserMenu()}
                </div>
                <div className={`top-bar-expand top-bar-expand__absolute`}>
                    {isShowFullTitle ? <div className="top-bar-expand__full-title">{title}</div> : null}
                </div>
            </div>
        );
    };

    return render();
}

export default WrapperUseMobileIframeLandscapeDisplayManager(TopBar);
